import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import UpgradeHotspot from "../common/UpgradeHotspot";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const axiosInstance = axios.create({
  withCredentials: true,
});

axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const SurveyBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiQuestionCount, setAiQuestionCount] = useState(10);
  const [generating, setGenerating] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState([]);
  const [metadataCollectionMode, setMetadataCollectionMode] = useState('none');
  const [allowAiAssistance, setAllowAiAssistance] = useState(true);
  const [questionBank, setQuestionBank] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showQuestionBank, setShowQuestionBank] = useState(false);
  const [mySurveys, setMySurveys] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState('');
  const [showMySurveys, setShowMySurveys] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [maxQuestions, setMaxQuestions] = useState(10);
  const [companyName, setCompanyName] = useState('');
  const [companyLogoUrl, setCompanyLogoUrl] = useState('');
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const [savingBranding, setSavingBranding] = useState(false);

  useEffect(() => {
    loadQuestionBank();
    loadMySurveys();
    loadSubscriptionInfo();
    loadCompanyBranding();
    
    if (location.state?.duplicateSurvey) {
      const survey = location.state.duplicateSurvey;
      setTitle(survey.title + ' (Copy)');
      setDescription(survey.description || '');
      setQuestions(survey.questions.map(q => ({
        ...q,
        id: Date.now().toString() + Math.random()
      })));
    }
  }, [location]);

  const loadCompanyBranding = async () => {
    try {
      const response = await axiosInstance.get(`${API}/user/branding`);
      setCompanyName(response.data.company_name || '');
      setCompanyLogoUrl(response.data.company_logo_url || '');
    } catch (error) {
      console.error('Failed to load company branding');
    }
  };

  const handleLogoUpload = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 500 * 1024) {
      toast.error('Logo must be under 500KB. Please use a smaller image.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => setCompanyLogoUrl(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleLogoDragOver = (e) => { e.preventDefault(); setIsDraggingLogo(true); };
  const handleLogoDragLeave = (e) => { e.preventDefault(); setIsDraggingLogo(false); };
  const handleLogoDrop = (e) => { e.preventDefault(); setIsDraggingLogo(false); handleLogoUpload(e.dataTransfer.files[0]); };
  const handleLogoFileSelect = (e) => handleLogoUpload(e.target.files[0]);
  const removeLogo = () => setCompanyLogoUrl('');

  const saveCompanyBranding = async () => {
    setSavingBranding(true);
    try {
      await axiosInstance.put(`${API}/user/branding`, {
        company_name: companyName || null,
        company_logo_url: companyLogoUrl || null
      });
      toast.success('Company branding saved!');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Company branding is only available for Sergeant and Lieutenant subscribers.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save branding');
      }
    } finally {
      setSavingBranding(false);
    }
  };

  const loadQuestionBank = async () => {
    try {
      const response = await axiosInstance.get(`${API}/question-bank`);
      setQuestionBank(response.data);
    } catch (error) {
      console.error('Failed to load question bank');
    }
  };

  const loadMySurveys = async () => {
    try {
      const response = await axiosInstance.get(`${API}/surveys`);
      setMySurveys(response.data);
    } catch (error) {
      console.error('Failed to load surveys');
    }
  };

  const loadSubscriptionInfo = async () => {
    try {
      const response = await axiosInstance.get(`${API}/subscription/status`);
      setSubscriptionInfo(response.data);
      setMaxQuestions(response.data.max_questions_per_survey);
    } catch (error) {
      console.error('Failed to load subscription info:', error);
      setMaxQuestions(10);
    }
  };

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) { toast.error('Please enter a prompt'); return; }
    setGenerating(true);
    try {
      const response = await axiosInstance.post(`${API}/ai/generate-survey`, {
        prompt: aiPrompt,
        question_count: aiQuestionCount
      });
      setTitle(response.data.title);
      setDescription(response.data.description || '');
      setQuestions(response.data.questions || []);
      toast.success('Survey generated!');
    } catch (error) {
      toast.error('Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const addQuestionFromBank = (question) => {
    if (questions.length >= maxQuestions) {
      const planType = subscriptionInfo?.has_active_subscription ? 'Pro' : 'Free';
      toast.error(`${planType} allows maximum ${maxQuestions} questions per survey. Upgrade to Pro for 50 questions.`);
      return;
    }
    setQuestions([...questions, { ...question, id: Date.now().toString() + Math.random() }]);
    toast.success('Question added!');
  };

  const addQuestionFromSurvey = (question) => {
    if (questions.length >= maxQuestions) {
      const planType = subscriptionInfo?.has_active_subscription ? 'Pro' : 'Free';
      toast.error(`${planType} allows maximum ${maxQuestions} questions per survey. Upgrade to Pro for 50 questions.`);
      return;
    }
    setQuestions([...questions, { ...question, id: Date.now().toString() + Math.random() }]);
    toast.success('Question added from survey!');
  };

  const loadAllQuestionsFromSurvey = (surveyId) => {
    const survey = mySurveys.find(s => s.id === surveyId);
    if (survey) {
      const newQuestions = survey.questions.map(q => ({ ...q, id: Date.now().toString() + Math.random() }));
      if (questions.length + newQuestions.length > maxQuestions) {
        const planType = subscriptionInfo?.has_active_subscription ? 'Pro' : 'Free';
        toast.error(`Adding these questions would exceed the ${maxQuestions} question limit for ${planType} plan.`);
        return;
      }
      setQuestions([...questions, ...newQuestions]);
      toast.success(`${newQuestions.length} questions added from "${survey.title}"!`);
      setShowMySurveys(false);
    }
  };

  const addTemplateFromBank = (category) => {
    const template = questionBank[category];
    setTitle(template.name);
    setDescription(template.description);
    setQuestions(template.questions.map((q, index) => ({ ...q, id: `${Date.now()}-${index}` })));
    toast.success(`${template.name} template loaded!`);
    setShowQuestionBank(false);
  };

  const addQuestion = () => {
    if (questions.length >= maxQuestions) {
      const planType = subscriptionInfo?.has_active_subscription ? 'Pro' : 'Free';
      toast.error(`${planType} allows maximum ${maxQuestions} questions per survey. ${!subscriptionInfo?.has_active_subscription ? 'Upgrade to Pro for 50 questions.' : ''}`);
      return;
    }
    setQuestions([...questions, { id: Date.now().toString(), question_text: '', question_type: 'text', options: null, required: true }]);
  };

  const removeQuestion = (index) => setQuestions(questions.filter((_, i) => i !== index));

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;
    if (field === 'question_type') {
      updated[index].options = value === 'multiple_choice' ? ['Option 1', 'Option 2'] : null;
    }
    setQuestions(updated);
  };

  const handleSave = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    if (questions.length === 0) { toast.error('Add at least one question'); return; }
    try {
      const isPaidUser = subscriptionInfo && 
        ['sergeant', 'lieutenant', 'monthly', 'yearly'].includes(subscriptionInfo.subscription_status);
      await axiosInstance.post(`${API}/surveys`, {
        title, description, questions,
        metadata_collection_mode: metadataCollectionMode,
        allow_ai_assistance: isPaidUser ? allowAiAssistance : false
      });
      toast.success('Survey created!');
      navigate('/dashboard');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Free plan limit reached! Upgrade to create more surveys.');
        setTimeout(() => navigate('/dashboard'), 2000);
      } else if (error.response?.status === 400 && error.response?.data?.detail?.includes('questions')) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to save survey');
      }
    }
  };

  const isPaidUser = subscriptionInfo && ['sergeant', 'lieutenant', 'monthly', 'yearly'].includes(subscriptionInfo.subscription_status);
  const isFreeUser = subscriptionInfo && ['free', 'recruit'].includes(subscriptionInfo.subscription_status);

  return (
    <div className="dashboard-container" data-testid="survey-builder">
      <div className="builder-container">
        <div className="builder-header">
          <button className="back-btn" onClick={() => navigate('/dashboard')} data-testid="back-button">
            ← Back to Dashboard
          </button>
        </div>

        <div className="ai-generate-section">
          <h2>Build with AI</h2>
          <input type="text" className="ai-input" placeholder="e.g., NPS survey for a coffee shop after purchase"
            value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} data-testid="ai-prompt-input" />
          <div className="ai-controls">
            <div className="question-count-control">
              <label htmlFor="question-count" className="question-count-label">Number of Questions:</label>
              <input id="question-count" type="number" min="1" max="25" className="question-count-input"
                value={aiQuestionCount} onChange={(e) => { const v = parseInt(e.target.value); if (v >= 1 && v <= 25) setAiQuestionCount(v); }}
                data-testid="question-count-input" />
              <span className="question-count-hint">(Max: 25)</span>
            </div>
            <button className="ai-generate-btn" onClick={handleAIGenerate} disabled={generating} data-testid="ai-generate-button">
              {generating ? 'Generating...' : 'Generate Survey with AI'}
            </button>
          </div>
        </div>

        {/* Question Bank Section */}
        <div className="question-bank-section">
          <button className="question-bank-toggle-btn" onClick={() => setShowQuestionBank(!showQuestionBank)} data-testid="question-bank-toggle">
            📚 {showQuestionBank ? 'Hide' : 'Show'} Question Bank & Templates
            <span className="chevron-icon">{showQuestionBank ? '▲' : '▼'}</span>
          </button>
          {showQuestionBank && questionBank && (
            <div className="question-bank-container" data-testid="question-bank">
              <h3>Expert Templates</h3>
              <p className="question-bank-description">Load complete survey templates or add individual questions</p>
              <div className="template-grid">
                {Object.keys(questionBank).map((category) => (
                  <div key={category} className="template-card" data-testid={`template-${category}`}>
                    <h4>{questionBank[category].name}</h4>
                    <p>{questionBank[category].description}</p>
                    <span className="template-question-count">{questionBank[category].questions.length} questions</span>
                    <button className="load-template-btn" onClick={() => addTemplateFromBank(category)} data-testid={`load-template-${category}`}>Load Template</button>
                    <button className="browse-questions-btn" onClick={() => setSelectedCategory(category === selectedCategory ? '' : category)} data-testid={`browse-${category}`}>
                      {selectedCategory === category ? 'Hide Questions' : 'Browse Questions'}
                    </button>
                    {selectedCategory === category && (
                      <div className="question-list">
                        {questionBank[category].questions.map((q, idx) => (
                          <div key={idx} className="question-bank-item" data-testid={`bank-question-${idx}`}>
                            <div className="question-bank-text">{q.question_text}</div>
                            <div className="question-bank-meta">
                              <span className="question-type-badge">{q.question_type}</span>
                              <button className="add-from-bank-btn" onClick={() => addQuestionFromBank(q)} data-testid={`add-bank-question-${idx}`}>+ Add</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* My Surveys Section */}
        <div className="question-bank-section">
          <button className="my-surveys-toggle-btn" onClick={() => setShowMySurveys(!showMySurveys)} data-testid="my-surveys-toggle">
            📋 {showMySurveys ? 'Hide' : 'Show'} My Surveys - Add Questions
            <span className="chevron-icon">{showMySurveys ? '▲' : '▼'}</span>
          </button>
          {showMySurveys && mySurveys.length > 0 && (
            <div className="question-bank-container" data-testid="my-surveys-section">
              <h3>Your Existing Surveys</h3>
              <p className="question-bank-description">Browse your surveys and add questions to this new survey</p>
              <div className="template-grid">
                {mySurveys.map((survey) => (
                  <div key={survey.id} className="template-card" data-testid={`my-survey-${survey.id}`}>
                    <h4>{survey.title}</h4>
                    <p>{survey.description || 'No description'}</p>
                    <span className="template-question-count">{survey.questions.length} questions</span>
                    <button className="load-template-btn" onClick={() => loadAllQuestionsFromSurvey(survey.id)} data-testid={`add-all-from-${survey.id}`}>Add All Questions</button>
                    <button className="browse-questions-btn" onClick={() => setSelectedSurvey(selectedSurvey === survey.id ? '' : survey.id)} data-testid={`browse-survey-${survey.id}`}>
                      {selectedSurvey === survey.id ? 'Hide Questions' : 'Browse Questions'}
                    </button>
                    {selectedSurvey === survey.id && (
                      <div className="question-list">
                        {survey.questions.map((q, idx) => (
                          <div key={idx} className="question-bank-item" data-testid={`survey-question-${idx}`}>
                            <div className="question-bank-text">{q.question_text}</div>
                            <div className="question-bank-meta">
                              <span className="question-type-badge">{q.question_type}</span>
                              <button className="add-from-bank-btn" onClick={() => addQuestionFromSurvey(q)} data-testid={`add-survey-question-${idx}`}>+ Add</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {showMySurveys && mySurveys.length === 0 && (
            <div className="empty-state"><p>No surveys yet. Create your first one to reuse questions later!</p></div>
          )}
        </div>

        <div className="survey-form">
          <div className="form-group">
            <label className="form-label">Survey Title *</label>
            <input type="text" className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="survey-title-input" />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-textarea" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="survey-description-input" />
          </div>

          <h3>Questions</h3>
          {questions.map((q, index) => (
            <div key={q.id} className="question-item" data-testid={`question-item-${index}`}>
              <div className="question-header">
                <span>Question {index + 1}</span>
                <button className="remove-question-btn" onClick={() => removeQuestion(index)} data-testid={`remove-question-${index}`}>Remove</button>
              </div>
              <div className="form-group">
                <label className="form-label">Question Text</label>
                <input type="text" className="form-input" value={q.question_text} onChange={(e) => updateQuestion(index, 'question_text', e.target.value)} data-testid={`question-text-${index}`} />
              </div>
              <div className="form-group">
                <label className="form-label">Question Type</label>
                <select className="form-input" value={q.question_type} onChange={(e) => updateQuestion(index, 'question_type', e.target.value)} data-testid={`question-type-${index}`}>
                  <option value="text">Text</option>
                  <option value="multiple_choice">Multiple Choice</option>
                  <option value="rating">Rating (1-5)</option>
                  <option value="nps">NPS (0-10)</option>
                </select>
              </div>
              {q.question_type === 'multiple_choice' && q.options && (
                <div className="form-group">
                  <label className="form-label">Options (comma-separated)</label>
                  <input type="text" className="form-input" value={q.options.join(', ')} onChange={(e) => updateQuestion(index, 'options', e.target.value.split(',').map(s => s.trim()))} data-testid={`question-options-${index}`} />
                </div>
              )}
            </div>
          ))}

          <div className="question-limit-info">
            <span className={questions.length >= maxQuestions ? 'limit-reached' : 'limit-ok'}>
              {questions.length} / {maxQuestions} questions
              {isFreeUser && (
                <UpgradeHotspot feature="50 Questions per Survey" description="Recruit plan allows only 10 questions. Upgrade to Sergeant or Lieutenant for up to 50 questions per survey!"
                  currentTier={subscriptionInfo.subscription_status} requiredTier="sergeant" onUpgrade={() => navigate('/manage-subscription')} position="inline">
                  <span className="upgrade-hint"> (Upgrade for 50 questions)</span>
                </UpgradeHotspot>
              )}
            </span>
          </div>

          <button className="add-question-btn" onClick={addQuestion} disabled={questions.length >= maxQuestions} data-testid="add-question-button">+ Add Question</button>

          <div className="metadata-collection-section">
            <h3 className="metadata-section-title">Respondent Data Collection Settings</h3>
            <p className="metadata-section-description">Choose how you want to collect technical data (IP address, browser, device type) from survey respondents:</p>
            <div className="metadata-options">
              <label className="metadata-option">
                <input type="radio" name="metadata-mode" value="none" checked={metadataCollectionMode === 'none'} onChange={(e) => setMetadataCollectionMode(e.target.value)} data-testid="metadata-none" />
                <div className="metadata-option-content">
                  <span className="metadata-option-title">🔒 Fully Anonymous (No Tracking)</span>
                  <span className="metadata-option-description">Do not collect any metadata. Survey takers will see a notice that the survey is completely anonymous with no cookies or tracking.</span>
                </div>
              </label>
              <label className="metadata-option">
                <input type="radio" name="metadata-mode" value="anonymous" checked={metadataCollectionMode === 'anonymous'} onChange={(e) => setMetadataCollectionMode(e.target.value)} data-testid="metadata-anonymous" />
                <div className="metadata-option-content">
                  <span className="metadata-option-title">👁️ Silent Collection (No Disclosure)</span>
                  <span className="metadata-option-description">Collect metadata without disclosure. No notice shown to respondents. Data remains anonymous but collected for analytics.</span>
                </div>
              </label>
              <label className="metadata-option">
                <input type="radio" name="metadata-mode" value="disclosed" checked={metadataCollectionMode === 'disclosed'} onChange={(e) => setMetadataCollectionMode(e.target.value)} data-testid="metadata-disclosed" />
                <div className="metadata-option-content">
                  <span className="metadata-option-title">📋 Disclosed Collection (Transparent)</span>
                  <span className="metadata-option-description">Collect metadata and inform respondents. A privacy notice will be displayed stating that technical data is collected for fraud prevention and analytics.</span>
                </div>
              </label>
            </div>
          </div>

          {/* AI Assistance Settings - Only for paid subscribers */}
          {isPaidUser && (
            <div className="ai-assistance-section" data-testid="ai-assistance-section">
              <h3 className="section-title">🤖 AI Assistance for Respondents</h3>
              <p className="section-description">Control whether survey takers can use AI to help rewrite their responses for clarity.</p>
              <div className="ai-toggle-container">
                <label className="ai-toggle-label">
                  <div className="toggle-switch-wrapper">
                    <input type="checkbox" checked={allowAiAssistance} onChange={(e) => setAllowAiAssistance(e.target.checked)} data-testid="allow-ai-toggle" />
                    <span className="toggle-slider-custom"></span>
                  </div>
                  <div className="toggle-text">
                    <span className="toggle-status">{allowAiAssistance ? 'Enabled' : 'Disabled'}</span>
                    <span className="toggle-description">{allowAiAssistance ? 'Respondents can use AI to improve their text responses' : 'Respondents will write responses without AI assistance'}</span>
                  </div>
                </label>
              </div>
            </div>
          )}
          
          {/* AI Assistance Upgrade Notice for Free/Recruit users */}
          {isFreeUser && (
            <UpgradeHotspot feature="AI Assistance for Respondents" description="Enable AI-powered response improvement for your survey takers. Help respondents articulate their feedback more clearly!"
              currentTier={subscriptionInfo.subscription_status} requiredTier="sergeant" onUpgrade={() => navigate('/manage-subscription')} position="inline">
              <div className="ai-upgrade-notice" data-testid="ai-upgrade-notice">
                <p>🔒 <strong>AI Assistance for Respondents</strong> is available for Sergeant and Lieutenant subscribers.</p>
                <p className="upgrade-hint-text">Upgrade to let survey takers use AI to improve their responses.</p>
              </div>
            </UpgradeHotspot>
          )}

          {/* Company Branding Section - Only for paid subscribers */}
          {isPaidUser && (
            <div className="company-branding-section" data-testid="company-branding-section">
              <h3 className="branding-section-title">🏢 Company Branding</h3>
              <p className="branding-section-description">Personalize your surveys with your company name and logo. This will appear on all your survey pages.</p>
              <div className="branding-form">
                <div className="form-group">
                  <label className="form-label">Company Name</label>
                  <input type="text" className="form-input" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Enter your company name" data-testid="company-name-input" />
                </div>
                <div className="form-group">
                  <label className="form-label">Company Logo (Square, max 500KB)</label>
                  <div className={`logo-upload-area ${isDraggingLogo ? 'dragging' : ''} ${companyLogoUrl ? 'has-logo' : ''}`}
                    onDragOver={handleLogoDragOver} onDragLeave={handleLogoDragLeave} onDrop={handleLogoDrop} data-testid="logo-upload-area">
                    {companyLogoUrl ? (
                      <div className="logo-preview-container">
                        <img src={companyLogoUrl} alt="Company Logo" className="logo-preview" data-testid="logo-preview" />
                        <button type="button" className="remove-logo-btn" onClick={removeLogo} data-testid="remove-logo-btn">✕</button>
                      </div>
                    ) : (
                      <div className="logo-upload-placeholder">
                        <span className="upload-icon">📷</span>
                        <p>Drag & drop your logo here</p>
                        <p className="upload-hint">or click to select</p>
                        <input type="file" accept="image/*" onChange={handleLogoFileSelect} className="logo-file-input" data-testid="logo-file-input" />
                      </div>
                    )}
                  </div>
                </div>
                <button type="button" className="save-branding-btn" onClick={saveCompanyBranding} disabled={savingBranding} data-testid="save-branding-btn">
                  {savingBranding ? 'Saving...' : '💾 Save Branding'}
                </button>
                {(companyName || companyLogoUrl) && (
                  <div className="branding-preview" data-testid="branding-preview">
                    <p className="preview-label">Preview on survey:</p>
                    <div className="branding-preview-header">
                      <div className="branding-left">
                        {companyLogoUrl && <img src={companyLogoUrl} alt="Logo" className="preview-logo" />}
                        {companyName && <span className="preview-company-name">{companyName}</span>}
                      </div>
                      <div className="branding-right">
                        <span className="powered-by-text">powered by</span>
                        <img src="/cxrecon-logo-new.png" alt="CXrecon" className="powered-by-logo" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Show upgrade message for non-paid users */}
          {isFreeUser && (
            <UpgradeHotspot feature="Company Branding" description="Add your company name and logo to personalize surveys with your brand identity. Build trust with respondents!"
              currentTier={subscriptionInfo.subscription_status} requiredTier="sergeant" onUpgrade={() => navigate('/manage-subscription')} position="inline">
              <div className="branding-upgrade-notice" data-testid="branding-upgrade-notice">
                <p>🔒 <strong>Company Branding</strong> is available for Sergeant and Lieutenant subscribers.</p>
                <p className="upgrade-hint-text">Upgrade to add your company name and logo to surveys.</p>
              </div>
            </UpgradeHotspot>
          )}

          <button className="save-survey-btn" onClick={handleSave} data-testid="save-survey-button">Save Survey</button>
        </div>
      </div>
    </div>
  );
};

export default SurveyBuilder;
